<template>
  <div>
    <static-alert :message="message" @on-hide="message=''" />
    <div class="chart-card">
      <div class="d-flex flex-row align-items-center">
        <h6 class="chart-title">On the Shelf</h6>
        <div v-if="snapshot.length > 0" class="ms-auto text-end text-muted">
          Total active listings: <strong>{{ totalItems.toLocaleString() }}</strong>
        </div>
      </div>
      <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
        <loading-spinner />
      </div>
      <template v-else-if="snapshot.length > 0">
        <apexchart
          type="bar"
          height="280"
          :options="chartOptions"
          :series="chartSeries"
        />
      </template>
      <div v-else class="text-muted text-center py-5">No data</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from "vue"
import { useStore } from "vuex"
import StaticAlert from "@/app/components/Utils/StaticAlert.vue"
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue"

const apexchart = defineAsyncComponent(() =>
  import('vue3-apexcharts').then(module => module.default || module)
)

const store = useStore()
const message = ref('')
const loading = ref(false)
const snapshot = ref([])

const totalItems = computed(() => snapshot.value.reduce((sum, row) => sum + row.item_count, 0))

// Graduated palette: healthy (green) → poor health (red)
const agingColors = [
  '#7dd89a', // 0-30d    — green
  '#b0d96e', // 31-60d   — yellow-green
  '#e8d454', // 61-90d   — yellow
  '#fcc07a', // 91-180d  — orange
  '#f09060', // 181-365d — dark orange
  '#e88080', // 365+d    — red
]

const chartSeries = computed(() => [{
  name: 'Items',
  data: snapshot.value.map(row => row.item_count)
}])

const chartOptions = computed(() => ({
  chart: {
    type: 'bar',
    toolbar: { show: false },
    animations: { enabled: true }
  },
  plotOptions: {
    bar: {
      barHeight: '95%',
      borderRadius: 2,
      horizontal: true,
      distributed: true,
      dataLabels: { position: 'top' }
    }
  },
  colors: agingColors,
  dataLabels: {
    enabled: true,
    textAnchor: 'start',
    offsetX: 15,
    style: {
      fontSize: '12px',
      colors: ['#000']
    },
    dropShadow: {
      enabled: true,
      top: 0,
      left: 0,
      blur: 2,
      color: '#fff',
      opacity: 1
    },
    formatter: (val, { dataPointIndex }) => {
      const row = snapshot.value[dataPointIndex]
      return `${val}  (${row?.pct_of_total}%)`
    }
  },
  xaxis: {
    categories: snapshot.value.map(row => row.age_bucket),
    labels: {
      formatter: (val) => Math.round(val)
    }
  },
  yaxis: {
    labels: {
      style: { fontSize: '12px' }
    }
  },
  tooltip: {
    y: {
      formatter: (val, { dataPointIndex }) => {
        const row = snapshot.value[dataPointIndex]
        return `${val} items (${row?.pct_of_total}%)`
      }
    }
  },
  legend: { show: false },
  grid: {
    borderColor: '#e0e0e0',
    strokeDashArray: 4
  }
}))

onMounted(() => {
  fetchSnapshot()
})

function fetchSnapshot() {
  loading.value = true
  store.dispatch("eshop/getInventoryHealthSnapshot")
    .then(data => {
      snapshot.value = data.snapshot || []
    })
    .catch(error => {
      message.value = error.response?.data?.error || error.message || 'Failed to load inventory health data'
      console.error('Failed to load inventory health snapshot:', error)
    })
    .finally(() => {
      loading.value = false
    })
}
</script>
